import React from "react"
import Button from "@mui/material/Button"
import {
  Paper,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import LocationIcon from "@mui/icons-material/GpsFixed"
import HomeIcon from "@mui/icons-material/Home"
import { red, purple } from "@mui/material/colors"

import { formatDate } from "../../../utils/dateUtils"
import { getInstructorFullNameLookup } from "../../../utils/instructorUtils"
import LessonEditWrapper from "../../admin/common/LessonEditWrapper"
import { useSelectorTyped } from "../../../store/configureStore"
import { SelfBookingSharedLessonDialog } from "./SelfBookingSharedLessonDialog"
import {
  GenericStringObj,
  LessonType,
  LessonTypeName,
  SlotLocation,
  SlotToPick,
  StateConfig,
} from "../../../types"

// This combines the date and time buttons into one component, so we always
// show the times for every date available.

type LocationGroup = {
  id: number
  addressLabel: string
  meetingPointLabel: string
  distanceMiles: number
  slots: SlotToPick[]
}

const groupSlots = (
  slots: SlotToPick[],
  locations: SlotLocation[],
): LocationGroup[] => {
  const groups: { [key: string]: SlotToPick[] } = {}
  slots.forEach((slot) => {
    const { address } = slot.location
    if (!(address in groups)) {
      groups[address] = []
    }
    groups[address].push(slot)
  })
  return locations
    .map((location, i) => ({
      id: i,
      addressLabel: location.address.split(",").splice(0, 2).join(","),
      meetingPointLabel: location.meetingPointLabel,
      distanceMiles: location.distanceMiles,
      slots: groups[location.address],
    }))
    .sort((a, b) => a.distanceMiles - b.distanceMiles)
}

type Props = {
  slots: SlotToPick[]
  showNames: boolean
  timeSelectedCallback: (slot: SlotToPick) => void
  lessonType: LessonType
  showTimeZones: boolean
  stateConfig: StateConfig
}

const DateTimesList = ({
  slots,
  showNames,
  timeSelectedCallback,
  lessonType,
  showTimeZones = false,
  stateConfig,
}: Props) => {
  const theme = useTheme()
  const sortedTimes = slots.sort((a, b) => a.startTime - b.startTime)
  const timeFormat = showTimeZones ? "time" : "h:mma"
  const { isAdmin } = useSelectorTyped((state) => state.firebase.profile)
  const [instructorFullNameLookup, setInstructorFullNameLookup] =
    React.useState<GenericStringObj | null>(null)
  const [adminUnpairedLessonDetails, setAdminUnpairedLessonDetails] =
    React.useState<GenericStringObj | null>(null)
  const [studentUnpairedLessonDetails, setStudentUnpairedLessonDetails] =
    React.useState<SlotToPick | null>(null)

  // This is to group by pickup location.
  const locations: SlotLocation[] = slots.map((slot) => slot.location)
  const locationsUnique = locations.filter(
    (v, i, a) => a.findIndex((t) => t.address === v.address) === i,
  )
  const locationGroups = groupSlots(sortedTimes, locationsUnique)

  const fetchInstructorData = async () => {
    const fullNameLookup = await getInstructorFullNameLookup()
    setInstructorFullNameLookup(fullNameLookup)
  }

  React.useEffect(() => {
    if (showNames) {
      fetchInstructorData()
    }
  }, [showNames])

  const renderGroupLabel = (group: LocationGroup) => (
    <>
      <div className="date-time-group-label">
        {group.distanceMiles === 0 && stateConfig.hasHomePickup ? (
          <>
            <HomeIcon style={{ marginRight: 4, fontSize: 14 }} />
            {group.meetingPointLabel}
          </>
        ) : (
          <>
            <LocationIcon style={{ marginRight: 4, fontSize: 14 }} />
            Meet at {group.meetingPointLabel}
          </>
        )}
      </div>
      {group.distanceMiles > 0 && (
        <div style={{ fontSize: 12, marginBottom: 20 }}>
          {group.addressLabel}
          <br />
          {group.distanceMiles.toFixed(1)} mile
          {group.distanceMiles === 1 ? "" : "s"} away
        </div>
      )}
    </>
  )

  const handleSelectSlot = async (slot: SlotToPick) => {
    if (lessonType.name === LessonTypeName.Shared) {
      if (isAdmin && slot.isUnpaired && slot.lessonId) {
        setAdminUnpairedLessonDetails({
          lessonId: slot.lessonId,
          instructorId: slot.instructorId,
        })
      } else {
        setStudentUnpairedLessonDetails(slot)
      }
    } else {
      timeSelectedCallback(slot)
    }
  }

  const handleAdminUnpairedDialogClose = () => {
    setAdminUnpairedLessonDetails(null)
  }

  const handleStudentUnpairedDialogClose = () => {
    setStudentUnpairedLessonDetails(null)
  }

  const renderButtonForSlotType = (
    group: LocationGroup,
    slot: SlotToPick,
    label: string,
  ) => {
    let backgroundColor
    let color
    if (slot.isPrivate) {
      // eslint-disable-next-line prefer-destructuring
      backgroundColor = red[500]
    }
    if (slot.isUnpaired) {
      // eslint-disable-next-line prefer-destructuring
      backgroundColor = purple[500]
      color = "#ffffff"
    }

    const button = (
      <Button
        variant="contained"
        color="secondary"
        key={`${group.id}-${slot.startTime}`}
        style={{
          display: "block",
          margin: 5,
          marginLeft: "auto",
          marginRight: "auto",
          background: backgroundColor,
          textTransform: "none",
          minWidth: 86,
          color,
        }}
        onClick={() => handleSelectSlot(slot)}
      >
        <div dangerouslySetInnerHTML={{ __html: label }} />
      </Button>
    )

    if (slot.isUnpaired) {
      return (
        <Tooltip
          title={
            isAdmin
              ? "This is an unpaired lesson slot. Ensure the student has purchased a lesson package before booking."
              : "This is a shared lesson."
          }
        >
          <span>{button}</span>
        </Tooltip>
      )
    }
    return button
  }

  if (slots.length === 0) {
    return null
  }

  return (
    <>
      <div className="scrolling-date-header">
        {/* We just need to use any US-based timezone here. */}
        {formatDate(slots[0].startTime, slots[0].timeZone, "dddd")}
        <br />
        {formatDate(slots[0].startTime, slots[0].timeZone, "MMM D")}
      </div>
      <div data-cy="available-times">
        {locationGroups.map((group) => (
          <Paper
            key={group.id}
            elevation={0}
            style={{
              marginBottom: 10,
              padding: 10,
              background: "#f8f8f8",
              border:
                group.distanceMiles > 0
                  ? `1px solid ${theme.palette.accentTwo.main}`
                  : undefined,
            }}
          >
            {renderGroupLabel(group)}
            {group.slots.map((slot) => {
              if (!slot.startTime) return null
              const slotDurationMillis = slot.endTime - slot.startTime
              const slotDurationMinutes = slotDurationMillis / (60 * 1000)
              // This is to hack a bug where if you switch tabs during a search,
              // you can end up with a lesson duration different from what you intend.
              // Like you book a 2-hour lesson but it ends up as 3 hours on the schedule.
              if (slotDurationMinutes !== lessonType.durationMinutes)
                return null
              let label = formatDate(slot.startTime, slot.timeZone, timeFormat)
              if (showNames && instructorFullNameLookup) {
                const fullName = instructorFullNameLookup[slot.instructorId]
                label += `<br />${fullName}`
              }
              return renderButtonForSlotType(group, slot, label)
            })}
          </Paper>
        ))}
      </div>
      <Dialog
        open={adminUnpairedLessonDetails !== null}
        onClose={handleAdminUnpairedDialogClose}
        data-cy="edit-lesson-dialog"
      >
        <DialogContent>
          {adminUnpairedLessonDetails ? (
            <LessonEditWrapper
              lessonId={adminUnpairedLessonDetails.lessonId}
              instructorId={adminUnpairedLessonDetails.instructorId}
              onCloseCallback={handleAdminUnpairedDialogClose}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdminUnpairedDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {studentUnpairedLessonDetails ? (
        <SelfBookingSharedLessonDialog
          slot={studentUnpairedLessonDetails}
          onConfirmCallback={() => {
            timeSelectedCallback(studentUnpairedLessonDetails)
          }}
          onDismissCallback={handleStudentUnpairedDialogClose}
        />
      ) : null}
    </>
  )
}

export default DateTimesList
